import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extensions'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle, FontSize } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Mention from '@tiptap/extension-mention'
import clsx from 'clsx'

import { getSchema, rewriteUnknownContent } from '@tiptap/core'
import type { AnyExtension } from '@tiptap/core'
import type {
  RteProps,
  RteHandle,
  EditorContent as EditorContentType,
} from '../../types'
import {
  TimestampMark,
  applyTimestampMarks,
  timestampPluginKey,
} from '../../extensions/timestamp'
import { MergeTagNode } from '../../extensions/merge-tag'
import { CtaButtonNode } from '../../extensions/cta-button'
import { CustomImage } from '../../extensions/custom-image'
import { createMentionClickPlugin } from '../../extensions/mention-click'
import { toPlainText } from '../../serializers/plain-text'
import { toMjml } from '../../serializers/mjml'
import { sanitizeAndPrefixUrl } from '@disco/utils/links'
import type { ToolbarProps } from '../toolbar/types'
import { isDocBlank, resolveItems } from '../toolbar/utils'
import { useMentionSuggestion } from '../mention-list/use-mention-suggestion'
import styles from './editor.module.css'

const DEBOUNCE_MS = 150

function getContent(editor: Editor): EditorContentType {
  const json = editor.getJSON()
  return {
    json,
    html: editor.getHTML(),
    plainText: toPlainText(json),
  }
}

export type EditorCoreProps = RteProps & {
  /**
   * Renders the toolbar. Web passes the Radix one (see `../../web`); native
   * passes nothing, which is what keeps Radix out of the mobile bundle — Metro
   * has no tree-shaking, so a static import here ships and evaluates on every
   * cold start even when `showToolbar` is false.
   */
  renderToolbar?: (ctx: ToolbarProps) => React.ReactNode
}

export const RichTextEditorCore = forwardRef<RteHandle, EditorCoreProps>(
  function RichTextEditorCore(
    {
      type = 'default',
      readOnly = false,
      onChange,
      initialContent,
      placeholder: placeholderText,
      users = [],
      usersLoading = false,
      onInvite,
      onTimestampClick,
      onMentionClick,
      onSubmit,
      onCancel,
      showToolbar,
      className,
      onImageUpload,
      customToolbar,
      getCurrentTimestamp,
      isTimestampActive,
      trackDurationSeconds,
      submitText,
      showDividers,
      maxLength,
      renderToolbar,
      onMentionItemsChange,
      suppressWebMentionPopup,
    },
    ref,
  ) {
    const placeholder = placeholderText ?? 'Start writing...'
    // `renderToolbar` is part of the condition so the content area does not
    // reserve toolbar space on mobile, where nothing renders one.
    const shouldShowToolbar = (showToolbar ?? true) && renderToolbar != null
    const handleSubmitRef = useRef<() => void>(() => {})
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const onTimestampClickRef = useRef(onTimestampClick)
    onTimestampClickRef.current = onTimestampClick
    const onMentionClickRef = useRef(onMentionClick)
    onMentionClickRef.current = onMentionClick
    const onSubmitRef = useRef(onSubmit)
    onSubmitRef.current = onSubmit
    const trackDurationRef = useRef(trackDurationSeconds)
    trackDurationRef.current = trackDurationSeconds
    const placeholderRef = useRef(placeholder)
    placeholderRef.current = placeholder
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isMountedRef = useRef(true)
    const latestContentRef = useRef(initialContent ?? undefined)

    const editorRef = useRef<Editor | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
      return () => {
        isMountedRef.current = false
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
          try {
            if (
              editorRef.current &&
              !editorRef.current.isDestroyed &&
              onChangeRef.current
            ) {
              const content = getContent(editorRef.current)
              onChangeRef.current(content)
            }
          } catch {
            // Editor was already destroyed — nothing to flush
          }
        }
      }
    }, [])

    // Key on the toolbar item list contents so callers passing an inline array
    // literal don't force the editor to be re-created on every parent render.
    // `undefined` (mode defaults) is distinct from `[]` (all items disabled),
    // so they must produce different keys.
    const customToolbarKey =
      customToolbar === undefined ? 'default' : JSON.stringify(customToolbar)
    const enabledItems = useMemo(
      () => new Set(resolveItems(type, customToolbar)),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [type, customToolbarKey],
    )

    const { suggestion: mentionSuggestion, selectMention } =
      useMentionSuggestion(users, usersLoading, onInvite, {
        suppressPopup: suppressWebMentionPopup,
        onFilteredItemsChange: onMentionItemsChange,
      })

    const extensions = useMemo(() => {
      const mentionsEnabled = enabledItems.has('mention')
      const timestampEnabled =
        enabledItems.has('timestamp') || enabledItems.has('timestamp-range')
      const headingEnabled = enabledItems.has('heading')
      const boldEnabled = enabledItems.has('bold')
      const italicEnabled = enabledItems.has('italic')
      const bulletListEnabled = enabledItems.has('bullet-list')
      const orderedListEnabled = enabledItems.has('ordered-list')
      const imageEnabled = enabledItems.has('image')
      const linkEnabled = enabledItems.has('link')

      const exts: AnyExtension[] = [
        StarterKit.configure({
          heading: headingEnabled ? { levels: [1, 2, 3, 4] } : false,
          bold: boldEnabled ? {} : false,
          italic: italicEnabled ? {} : false,
          bulletList: bulletListEnabled ? {} : false,
          orderedList: orderedListEnabled ? {} : false,
          listItem: bulletListEnabled || orderedListEnabled ? {} : false,
          link: false,
          horizontalRule: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          strike: false,
        }),
        Placeholder.configure({
          placeholder: () => placeholderRef.current,
        }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        FontSize,
      ]

      if (linkEnabled) {
        exts.push(
          Link.extend({ inclusive: () => false }).configure({
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
            HTMLAttributes: {
              rel: 'noopener noreferrer',
              target: '_blank',
            },
          }),
        )
      }

      if (imageEnabled) {
        exts.push(CustomImage.configure({ inline: false, allowBase64: true }))
      }

      if (timestampEnabled) {
        exts.push(
          TimestampMark.configure({
            onTimestampClick: (tc: string) => onTimestampClickRef.current?.(tc),
            getTrackDurationSeconds: () => trackDurationRef.current,
          }),
        )
      }

      const ConfiguredMention = Mention.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            type: { default: 'user' },
            mentionSuggestionChar: { default: '@' },
          }
        },
        addProseMirrorPlugins() {
          return [
            ...(this.parent?.() ?? []),
            createMentionClickPlugin(
              () => onMentionClickRef.current,
              this.editor.isEditable,
            ),
          ]
        },
      })

      exts.push(
        ConfiguredMention.configure({
          HTMLAttributes: { class: 'mention' },
          deleteTriggerWithBackspace: true,
          suggestion: {
            ...mentionSuggestion,
            allow: () => mentionsEnabled,
          },
          renderHTML({ options, node }) {
            const char =
              (node.attrs.mentionSuggestionChar as string | undefined) ??
              options.suggestion.char
            return [
              'span',
              { class: 'mention', 'data-mention-id': node.attrs.id },
              `${char}${node.attrs.label ?? node.attrs.id}`,
            ]
          },
        }),
      )

      if (type === 'email') {
        exts.push(MergeTagNode, CtaButtonNode)
      }

      return exts
    }, [type, mentionSuggestion, enabledItems])

    const sanitizedContent = useMemo(() => {
      if (!latestContentRef.current) return undefined
      const schema = getSchema(extensions)
      const { json, rewrittenContent } = rewriteUnknownContent(
        JSON.parse(JSON.stringify(latestContentRef.current)),
        schema,
      )
      if (rewrittenContent.length > 0) {
        console.warn(
          '[rte] dropped unknown content',
          rewrittenContent.map(r => r.unsupported),
        )
      }
      return json ?? undefined
    }, [extensions])

    const editor = useEditor(
      {
        extensions,
        content: sanitizedContent,
        editable: !readOnly,
        editorProps: {
          attributes: {
            class: 'rte-prosemirror',
          },
        },
        onUpdate: ({ editor: ed }) => {
          latestContentRef.current = ed.getJSON()
          if (!onChangeRef.current) return
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
          }
          debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null
            if (
              !isMountedRef.current ||
              ed.isDestroyed ||
              editorRef.current !== ed
            )
              return
            const content = getContent(ed)
            if (type === 'email') {
              content.mjml = toMjml(content.json)
            }
            onChangeRef.current?.(content)
          }, DEBOUNCE_MS)
        },
      },
      [extensions],
    )

    useEffect(() => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
    }, [editor])

    editorRef.current = editor

    const handleSubmit = useCallback(() => {
      if (!editor || isDocBlank(editor)) return
      applyTimestampMarks(editor)
      const content = getContent(editor)
      if (type === 'email') {
        content.mjml = toMjml(content.json)
      }
      const result = onSubmitRef.current?.(content)
      if (result instanceof Promise) {
        setIsSubmitting(true)
        result
          .finally(() => {
            if (isMountedRef.current) setIsSubmitting(false)
          })
          .catch(() => {
            // Caller owns the returned promise; ignore rejection here so the
            // internal finally() chain doesn't surface as unhandled.
          })
      }
    }, [editor, type])

    handleSubmitRef.current = handleSubmit

    useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly)
      }
    }, [editor, readOnly])

    useEffect(() => {
      if (!editor) return
      editor.view.dispatch(
        editor.state.tr.setMeta(timestampPluginKey, { rebuild: true }),
      )
    }, [editor, trackDurationSeconds])

    const handleEditorClick = useCallback(
      (e: React.MouseEvent) => {
        if (!readOnly) return
        const target = e.target as HTMLElement

        const pm = target.closest('.ProseMirror')
        if (!pm) return
        const link =
          target instanceof HTMLAnchorElement ? target : target.closest('a')
        if (!link || !pm.contains(link)) return
        // Older notes may hold a bare "example.com", which the browser would
        // open as a relative link on our own domain.
        const safeHref = sanitizeAndPrefixUrl(link.getAttribute('href') ?? '')
        if (safeHref) {
          e.preventDefault()
          if (safeHref.startsWith('mailto:')) {
            window.location.href = safeHref
          } else {
            window.open(safeHref, '_blank', 'noopener,noreferrer')
          }
        }
      },
      [readOnly],
    )

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          editor?.commands.clearContent()
        },
        focus: () => {
          editor?.commands.focus()
        },
        setContent: content => {
          editor?.commands.setContent(content, { emitUpdate: true })
        },
        getEditor: () => editor,
        selectMention, // used for mobile native mention menu
      }),
      [editor, selectMention],
    )

    if (!editor) return null

    const wrapperClassName = clsx(styles.editorWrapper, className)

    const contentClassName = clsx(
      styles.editorContent,
      shouldShowToolbar && styles.editorContentWithToolbar,
      readOnly && styles.readOnly,
      readOnly && onMentionClick && styles.mentionsClickable,
    )

    return (
      <div className={wrapperClassName}>
        <div
          className={contentClassName}
          onClick={handleEditorClick}
          role={readOnly ? 'document' : undefined}
          tabIndex={readOnly ? 0 : undefined}
          data-rte-content // Used for mobile targeting rte styles
        >
          <EditorContent editor={editor} />
        </div>
        {shouldShowToolbar &&
          !readOnly &&
          renderToolbar?.({
            editor,
            mode: type,
            customToolbar,
            onSubmit: handleSubmit,
            onCancel,
            onImageUpload,
            getCurrentTimestamp,
            isTimestampActive,
            submitText,
            showDividers,
            maxLength,
            isSubmitting,
          })}
      </div>
    )
  },
)
